/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {PackagesCartItem} from "types/entities/PackagesCartItem";
import {useEffect, useState} from "react";
import {ArticleTicketType} from "types/enums/ArticleTicketType";
import {useDispatch} from "useDispatch";
import {translate} from "utils/utility";
import {PackageItemItemType} from "types/enums/PackageItemItemType";
import {ReservationDriver} from "types/entities/ReservationDriver";
import {Reservation} from "types/entities/Reservation";
import {useTypedSelector} from "helpers/reducers";
import {Option} from "types/entities/specific/Option";
import {UpsellOption} from "types/entities/specific/UpsellOption";
import BookingLoginAction from "store/bookingLogin/BookingLoginAction";
import {useTranslation} from "react-i18next";

export const useAssignedItems = (reservationDriver: ReservationDriver) => {
    const dispatch = useDispatch();
    const {t} = useTranslation();

    const selectedReservation: Reservation | null = useTypedSelector(state => state.bookingLoginState.selectedReservation);

    //these are always all items
    const [items, setItems] = useState<PackagesCartItem[]>([]);
    //these are always only my items
    const [reservationDriverItems, setReservationDriverItems] = useState<PackagesCartItem[]>([]);

    //there will be only one of these
    const selectedAccessTicket = reservationDriverItems.find(item => item.packageItem!.article.ticket_type === ArticleTicketType.Access) ?? null;
    const selectedAccessTicketId = selectedAccessTicket?.id ?? null;
    const [reservationDriverUpsellItems, setReservationDriverUpsellItems] = useState<PackagesCartItem[]>([]);
    //this is access ticket only
    const [selectedAccessItem, setSelectedAccessItem] = useState<number | null>(selectedAccessTicketId);

    //this is the current upsell selected
    const [selectedUpsellItem, setSelectedUpsellItem] = useState<number | null>(null);

    const setAllItems = () => {
        if (!selectedReservation || !selectedReservation.packagesCart || !reservationDriver) {
            return;
        }
        const packagesCart = selectedReservation.packagesCart;
        // console.log("setAllItems", packagesCart);
        if (packagesCart && packagesCart.packagesCartItems) {
            setItems(packagesCart.packagesCartItems);

            const driverItems = packagesCart.packagesCartItems.filter(item => item.reservation_driver_id == reservationDriver.id);
            setReservationDriverItems(driverItems);

            const selectedAccess = driverItems.find(item => item.packageItem!.article.ticket_type === ArticleTicketType.Access)?.id ?? null;

            setSelectedAccessItem(selectedAccess);
            const upsellItems = driverItems.filter(item => item.packageItem?.type == PackageItemItemType.Upsell);
            setReservationDriverUpsellItems(upsellItems);
        }
    };

    /**
     * Retrieves items matching the specified ticket type from the reservations list -- all items, they do not have to belong.
     *
     * @param {ArticleTicketType} ticket_type - The type of article ticket to match against.
     *
     * @param {boolean} includeOrExclude - if set true, the return will match the ticket_type, otherwise return all that aren't the ticket_type
     * @returns {Array} An array of items that match the provided ticket type. Returns an empty array if no matches are found.
     */
    const getItemsByType = (ticket_type: ArticleTicketType, includeOrExclude: "include" | "exclude" = "include"): Array<PackagesCartItem> => {
        return items
            ? items.filter(item =>
                  includeOrExclude == "include" ? item.packageItem?.article?.ticket_type === ticket_type : item.packageItem?.article.ticket_type !== ticket_type
              )
            : [];
    };

    const manageItemHandler = async (packages_cart_item_id: number, type: "add" | "remove") => {
        const r = await dispatch(
            BookingLoginAction.fetchAddOrEditCartItems(reservationDriver.reservation_id, reservationDriver.id, packages_cart_item_id, type)
        );
        // toast.success("Access item has been released");
        setItems(r.reservation.packagesCart!.packagesCartItems as PackagesCartItem[]);
        setSelectedUpsellItem(null);
        return true;
    };

    const accessItems = getItemsByType(ArticleTicketType.Access);
    const otherItems = getItemsByType(ArticleTicketType.Access, "exclude");
    const allUpsellItems = items
        .filter(item => item.packageItem?.type == PackageItemItemType.Upsell)
        .reduce((acc: UpsellOption[], item: PackagesCartItem) => {
            const existingItemIndex = acc.findIndex(accItem => accItem.package_item_id === item.package_item_id);
            if (existingItemIndex !== -1) {
                if (item.reservation_driver_id == reservationDriver.id) {
                    acc[existingItemIndex].usedValues.push(item.id);
                    acc[existingItemIndex].used_count++;
                } else {
                    item.reservation_driver_id == null ? acc[existingItemIndex].count++ : null;
                    item.reservation_driver_id == null && acc[existingItemIndex].freeValues.push(item.id);
                }
            } else {
                const used = item.reservation_driver_id !== null;
                acc.push({
                    label: item.packageItem?.article.article_name ? translate(item.packageItem?.article.article_name) : "",
                    usedValues: used ? [item.id] : [],
                    freeValues: used ? [] : [item.id],
                    package_item_id: item.package_item_id,
                    count: item.reservation_driver_id == reservationDriver.id ? 0 : item.reservation_driver_id == null ? 1 : 0,
                    used_count: item.reservation_driver_id == reservationDriver.id ? 1 : 0,
                });
            }
            return acc;
        }, [])
        .map(item => {
            return {
                label: item.label,
                count: item.count,
                package_item_id: item.package_item_id,
                usedValues: item.usedValues,
                freeValues: item.freeValues,
                used_count: item.used_count,
            };
        });

    const mapAndReduceItems = (items: PackagesCartItem[]): UpsellOption[] => {
        return items
            .filter(item => item.reservation_driver_id === null)
            .reduce((acc: UpsellOption[], item: PackagesCartItem) => {
                const existingItemIndex = acc.findIndex(accItem => accItem.package_item_id === item.package_item_id);

                if (existingItemIndex !== -1) {
                    acc[existingItemIndex].freeValues.push(item.id);
                    acc[existingItemIndex].count++;
                } else {
                    acc.push({
                        label: item.packageItem?.article.article_name ? translate(item.packageItem?.article.article_name) : "",
                        usedValues: [],
                        freeValues: [item.id],
                        package_item_id: item.package_item_id,
                        count: 1,
                        used_count: 0,
                    });
                }
                return acc;
            }, [])
            .map(item => {
                return {
                    label: item.label,
                    count: item.count,
                    package_item_id: item.package_item_id,
                    usedValues: item.usedValues,
                    freeValues: item.freeValues,
                    used_count: item.used_count,
                };
            });
    };

    const accessItemOptions: UpsellOption[] = accessItems.length > 0 ? mapAndReduceItems(accessItems) : [];
    const availableUpsellsOptions: UpsellOption[] =
        otherItems.length > 0
            ? mapAndReduceItems(
                  otherItems.filter(item => {
                      if (item.packageItem) {
                          return item.packageItem?.type == PackageItemItemType.Upsell;
                      }
                      return false;
                  })
              )
            : [];
    const usedUpsellItems = (package_item_id: number): PackagesCartItem[] => {
        if (!selectedReservation || !selectedReservation.packagesCart) {
            return [];
        }
        const packagesCart = selectedReservation.packagesCart;
        console.log(packagesCart);
        const driverItems = packagesCart.packagesCartItems.filter(item => item.reservation_driver_id == reservationDriver.id);

        const upsellItems = driverItems.filter(item => item.packageItem?.type == PackageItemItemType.Upsell);
        return upsellItems.filter(item => item.package_item_id === package_item_id);
    };

    const setSelectedAccessTicketHandler = (option: Option) => {
        setAllItems();
        setSelectedAccessItem(option.value);
        // props.setValueHandler(option.value);
    };

    const setSelectedUpsellItemHandler = (option: Option) => {
        setAllItems();
        setSelectedUpsellItem(option.value);
    };

    useEffect(() => {
        setAllItems();
    }, [selectedReservation]);

    return {
        accessItemOptions,
        accessItems,
        allUpsellItems,
        availableUpsellsOptions,
        otherItems,
        reservationDriver,
        reservationDriverItems,
        reservationDriverUpsellItems,
        usedUpsellItems,
        selectedAccessItem,
        selectedAccessTicket,
        selectedUpsellItem,
        manageItemHandler,
        setAllItems,
        setSelectedAccessTicketHandler,
        setSelectedUpsellItemHandler,
    };
};
